// utils/ShareOptimizer.js
// 📱 ADVANCED SHARING SYSTEM
// Platform-specific optimizations for maximum virality

import { getShareUrl } from './deepLinkConfig';

/**
 * Platform-specific image dimensions and requirements
 */
export const PLATFORM_SPECS = {
  instagram_feed: {
    width: 1080,
    height: 1080,
    format: 'square',
    maxSize: 8 * 1024 * 1024, // 8MB
    quality: 0.95
  },
  instagram_story: {
    width: 1080,
    height: 1920,
    format: 'story',
    maxSize: 8 * 1024 * 1024,
    quality: 0.95
  },
  twitter: {
    width: 1200,
    height: 675,
    format: 'twitter',
    maxSize: 5 * 1024 * 1024, // 5MB
    quality: 0.90
  },
  facebook: {
    width: 1200,
    height: 630,
    format: 'facebook',
    maxSize: 8 * 1024 * 1024,
    quality: 0.90
  },
  tiktok: {
    width: 1080,
    height: 1920,
    format: 'story',
    maxSize: 10 * 1024 * 1024,
    quality: 0.95
  },
  snapchat: {
    width: 1080,
    height: 1920,
    format: 'story',
    maxSize: 5 * 1024 * 1024,
    quality: 0.90
  }
};

/**
 * Generate shareable links with pre-filled text
 * Uses canonical short URL format: https://gweffit.com/t/{id}
 */
export function generateShareLinks(trial, verdict, imageUrl) {
  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const winnerPct = Math.round(verdict.winnerPct || 50);

  // Use canonical short URL format
  const trialUrl = getShareUrl('trial', trial.id);

  // Share text variations
  const shareTexts = {
    default: `The jury has spoken: ${winner} wins with ${winnerPct}% of votes! 🔥\n\n${trial.title}\n\nVote on more at gweffit.com`,
    short: `${winner} ${winnerPct}% 🔥 gweffit.com`,
    casual: `Just voted on "${trial.title}" and ${winner} destroyed with ${winnerPct}%! Check it out: gweffit.com`,
    hype: `🚨 BREAKING: ${winner} absolutely DOMINATED with ${winnerPct}% 🚨\n\n"${trial.title}"\n\nJoin the jury: gweffit.com`,
    question: `What do YOU think about "${trial.title}"? The majority said ${winner} (${winnerPct}%)... agree or nah? 🤔\n\ngweffit.com`
  };

  const encodedDefault = encodeURIComponent(shareTexts.default);
  const encodedShort = encodeURIComponent(shareTexts.short);
  const encodedUrl = encodeURIComponent(trialUrl);
  // eslint-disable-next-line no-unused-vars
  const encodedImage = encodeURIComponent(imageUrl);

  return {
    twitter: `https://twitter.com/intent/tweet?text=${encodedDefault}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodeURIComponent(trial.title)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedDefault}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedShort}`,
    email: `mailto:?subject=${encodeURIComponent(trial.title)}&body=${encodedDefault}`,
    sms: `sms:?body=${encodedDefault}`,

    // Pre-formatted texts for clipboard
    texts: shareTexts,

    // Direct trial URL for copy-to-clipboard
    directUrl: trialUrl,
  };
}

/**
 * Copy image to clipboard (for easy Instagram sharing)
 */
export async function copyImageToClipboard(imageBlob) {
  try {
    if (!navigator.clipboard || !navigator.clipboard.write) {
      throw new Error('Clipboard API not supported');
    }
    
    const item = new ClipboardItem({ 'image/png': imageBlob });
    await navigator.clipboard.write([item]);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Copy text to clipboard
 */
export async function copyTextToClipboard(text) {
  try {
    await navigator.navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy text:', error);
    
    // Fallback method
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (err) {
      document.body.removeChild(textarea);
      return false;
    }
  }
}

/**
 * Detect user's device and platform
 */
export function detectPlatform() {
  const ua = navigator.userAgent.toLowerCase();
  const platform = {
    ios: /iphone|ipad|ipod/.test(ua),
    android: /android/.test(ua),
    mobile: /mobile/.test(ua),
    instagram: /instagram/.test(ua),
    facebook: /fb|facebook/.test(ua),
    twitter: /twitter/.test(ua),
    desktop: true
  };
  
  platform.desktop = !platform.mobile;
  
  return platform;
}

/**
 * Get optimal share method based on platform
 */
export function getOptimalShareMethod() {
  const platform = detectPlatform();
  
  if (platform.instagram) {
    return {
      method: 'copy_and_open',
      instruction: 'Image copied! Opening Instagram...',
      action: () => window.open('instagram://story-camera', '_blank')
    };
  }
  
  if (platform.ios || platform.android) {
    return {
      method: 'native_share',
      instruction: 'Opening share menu...',
      action: async (imageBlob, text) => {
        if (navigator.share) {
          const file = new File([imageBlob], 'gweffit_verdict.png', { type: 'image/png' });
          await navigator.share({
            files: [file],
            text: text,
            title: 'GweffIT Verdict'
          });
        }
      }
    };
  }
  
  return {
    method: 'download',
    instruction: 'Downloading image...',
    action: (imageBlob) => {
      const url = URL.createObjectURL(imageBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gweffit_verdict_${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };
}

/**
 * Add watermark to image (with branding)
 */
export function addWatermark(canvas, options = {}) {
  const ctx = canvas.getContext('2d');
  const {
    text = 'gweffit.com',
    position = 'bottom-right',
    opacity = 0.7,
    fontSize = 24,
    color = '#ffffff'
  } = options;
  
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.fillStyle = color;
  ctx.textAlign = position.includes('right') ? 'right' : 'left';
  
  const padding = 20;
  const x = position.includes('right') ? canvas.width - padding : padding;
  const y = position.includes('bottom') ? canvas.height - padding : padding + fontSize;
  
  // Add shadow for better visibility
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;
  
  ctx.fillText(text, x, y);
  ctx.restore();
}

/**
 * Add QR code to image (for attribution)
 */
export async function addQRCode(canvas, trialUrl, options = {}) {
  const {
    size = 100,
    position = 'bottom-right',
    padding = 20
  } = options;
  
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(trialUrl)}`;
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      
      let x = position.includes('right') ? canvas.width - size - padding : padding;
      let y = position.includes('bottom') ? canvas.height - size - padding : padding;
      
      // Add white background for QR code
      ctx.fillStyle = 'white';
      ctx.fillRect(x - 5, y - 5, size + 10, size + 10);
      
      // Draw QR code
      ctx.drawImage(img, x, y, size, size);
      resolve();
    };
    img.onerror = reject;
    img.src = qrUrl;
  });
}

/**
 * Optimize image for web sharing
 */
export function optimizeImageForSharing(canvas, platform = 'instagram_feed') {
  const spec = PLATFORM_SPECS[platform];
  
  // Create optimized canvas
  const optimized = document.createElement('canvas');
  optimized.width = spec.width;
  optimized.height = spec.height;
  const ctx = optimized.getContext('2d');
  
  // Draw with proper scaling
  if (canvas.width !== spec.width || canvas.height !== spec.height) {
    ctx.drawImage(canvas, 0, 0, spec.width, spec.height);
  } else {
    ctx.drawImage(canvas, 0, 0);
  }
  
  // Convert to blob with quality settings
  return new Promise((resolve) => {
    optimized.toBlob(
      (blob) => resolve(blob),
      'image/png',
      spec.quality
    );
  });
}

/**
 * Generate multiple sizes for different platforms
 */
export async function generateMultiPlatformImages(canvas, trial) {
  const images = {};
  
  for (const [platform, spec] of Object.entries(PLATFORM_SPECS)) {
    const optimized = await optimizeImageForSharing(canvas, platform);
    images[platform] = {
      blob: optimized,
      url: URL.createObjectURL(optimized),
      size: optimized.size,
      spec: spec
    };
  }
  
  return images;
}

/**
 * Smart share with analytics tracking
 */
export async function smartShare(trial, verdict, imageBlob, method = 'auto') {
  const platform = detectPlatform();
  const shareMethod = method === 'auto' ? getOptimalShareMethod() : method;
  const shareLinks = generateShareLinks(trial, verdict, URL.createObjectURL(imageBlob));
  
  // Track share intent
  trackShareEvent(trial.id, verdict, shareMethod, platform);
  
  // Execute share based on method
  if (shareMethod.method === 'native_share' && navigator.share) {
    try {
      const file = new File([imageBlob], 'gweffit_verdict.png', { type: 'image/png' });
      await navigator.share({
        files: [file],
        text: shareLinks.texts.default,
        title: trial.title
      });
      return { success: true, method: 'native_share' };
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Native share failed:', error);
      }
      return { success: false, error: error.message };
    }
  }
  
  if (shareMethod.method === 'copy_and_open') {
    const copied = await copyImageToClipboard(imageBlob);
    if (copied) {
      shareMethod.action();
      return { success: true, method: 'copy_and_open' };
    }
  }
  
  // Fallback to download
  shareMethod.action(imageBlob);
  return { success: true, method: 'download' };
}

/**
 * Track share events for analytics
 */
function trackShareEvent(trialId, verdict, method, platform) {
  // Send to analytics
  if (window.gtag) {
    window.gtag('event', 'share', {
      content_type: 'verdict_meme',
      trial_id: trialId,
      winner: verdict.winner,
      share_method: method.method,
      platform: Object.keys(platform).filter(k => platform[k])[0]
    });
  }
  
  // Log for debugging
  console.log('Share event:', {
    trial: trialId,
    method: method.method,
    platform: platform
  });
}

/**
 * Generate Instagram Story-specific stickers
 */
export function generateStoryStickers(trial, verdict) {
  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const winnerPct = Math.round(verdict.winnerPct || 50);
  
  return {
    poll: {
      question: trial.title,
      options: [trial.sideA, trial.sideB],
      result: winner
    },
    quiz: {
      question: `Who won: ${trial.title}?`,
      answer: winner,
      percentage: winnerPct
    },
    countdown: {
      text: 'Vote ends in',
      time: new Date(trial.endsAt)
    },
    link: {
      text: 'Vote Now',
      url: getShareUrl('trial', trial.id)
    }
  };
}

/**
 * Pre-fill social media posts
 */
export function getPrefillTemplates(trial, verdict) {
  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const winnerPct = Math.round(verdict.winnerPct || 50);
  
  return {
    twitter: {
      casual: `Just saw the results: ${winner} wins ${winnerPct}%! 🔥\n\n"${trial.title}"\n\nWhat do you think? Vote: gweffit.com`,
      hype: `🚨 ${winner.toUpperCase()} DOMINATED WITH ${winnerPct}% 🚨\n\n${trial.title}\n\nJoin the debate: gweffit.com`,
      question: `Hot take: ${winner} won with ${winnerPct}%...\n\nBut is the jury right about "${trial.title}"?\n\nYou decide: gweffit.com`
    },
    instagram: {
      caption: `The people have spoken: ${winner} takes it with ${winnerPct}%! 🏆\n\n"${trial.title}"\n\nWhat's your verdict? Link in bio! 👆\n\n#GweffIT #PublicOpinion #YouDecide #${winner.replace(/\s+/g, '')}`,
      story: `${winner} ${winnerPct}% 🔥\n\nSwipe up to vote!`
    },
    tiktok: {
      caption: `${winner} won ${winnerPct}%! Do you agree? #GweffIT #${winner.replace(/\s+/g, '')} #Debate`,
      voiceover: `So the internet just voted on ${trial.title}, and ${winner} won with ${winnerPct}% of votes. But here's what I think...`
    }
  };
}

const ShareOptimizer = {
  PLATFORM_SPECS,
  generateShareLinks,
  copyImageToClipboard,
  copyTextToClipboard,
  detectPlatform,
  getOptimalShareMethod,
  addWatermark,
  addQRCode,
  optimizeImageForSharing,
  generateMultiPlatformImages,
  smartShare,
  generateStoryStickers,
  getPrefillTemplates
};

export default ShareOptimizer;
